<template>
  <b-container class="chat-box">
    <b-row>
      <b-col cols="12" lg="10" class="mx-auto">
        <div
          v-for="(msg, msgidx) in messages"
          :key="msgidx"
          class="chat-record mt-2"
        >
          <ChatMessageComponent
            :style="{
              '--main-color': mainColor,
              '--secondary-color': secondaryColor,
            }"
            :initMsg="msg"
            :key="msgidx"
            :msgidx="msgidx"
            :ref="`msgRef-${msg.name}`"
            :context="context"
            :condition="condition"
            @select-card="handleCardClick"
            @continue="continueChat"
            @scroll-to-bottom="scrollToBottom"
            @scroll-to-msg="scrollToMsg"
          />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
// Logged-out demo for the Sistema PIE platform. Reached when a visitor clicks
// the PIE teaser (see Default.changeView). It reuses the same machinery as the
// logged-in chatbots (ChatMessageComponent + a `questions` graph) so it looks
// identical. It is a hub: the visitor can read about each of the five services
// the platform combines, and every step reinforces that with the Sistema PIE
// their school has UNLIMITED uses of all of them. The platform is contracted
// per school, so the call to action is NOT a login — it's a quote request: the
// visitor leaves their email and we contact them (redpieService.sendSalesContact).
import ChatMessageComponent from "./ChatMessageComponent.vue";
import PieIntroAnimation from "./PieIntroAnimation.vue";
import ServiceInfoAnimation from "./ServiceInfoAnimation.vue";
import redpieService from "@/services/redpieService";
import informationService from "@/services/informationService";

// The hub menu services. `nextIndex` matches an informative step. Visited
// services are dropped from the menu on later turns (see getAnswers). The quote
// request is appended separately and always offered.
const MENU = [
  { label: "Eureka · diagnóstico DEA", nextIndex: "eureka_info" },
  { label: "Planificaciones de clase", nextIndex: "planif_info" },
  { label: "Pruebas informales", nextIndex: "materials_info" },
  { label: "Documentación PIE", nextIndex: "docs_info" },
  { label: "Corrección de test del Decreto 170", nextIndex: "tests_info" },
];

const buildMenu = (context) => {
  const answers = [];
  // The videocall request is the primary CTA: first, highlighted and pulsing.
  answers.push({
    name: "Agendar una videollamada",
    isSelected: false,
    class: "col-md-8 col-12 my-2",
    bootstrap_classes: "text-left px-2 font-weight-bold",
    cardClass: "highlighted-option pulse-option",
    nextQuestionIndex: "quote",
  });
  answers.push({
    name: "Solicitar una cotización",
    isSelected: false,
    class: "col-md-8 col-12 my-2",
    bootstrap_classes: "text-left px-2 font-weight-bold",
    cardClass: "highlighted-option",
    nextQuestionIndex: "bill",
  });
  MENU.forEach((item) => {
    if (context && item.nextIndex in context) return;
    answers.push({
      name: item.label,
      isSelected: false,
      class: "col-md-8 col-12 my-2",
      bootstrap_classes: "text-left px-2 font-weight-bold",
      nextQuestionIndex: item.nextIndex,
    });
  });
  return answers;
};

// Service explanation step: an animated, themeable info component (one animated
// scene per detail) rendered as a custom component. ChatMessageComponent only
// binds condition/context/canWork on the dynamic component, so the per-service
// config (color + feature scenes) is closed over in a tiny render wrapper here.
const serviceAnimStep = (name, text, color, features) => ({
  name,
  text,
  type: "custom-component",
  component: {
    name: `${name}Animation`,
    render(h) {
      return h(ServiceInfoAnimation, { props: { color, features } });
    },
  },
  single_choice: false,
  answersLoaded: true,
  answers: [],
  // Sin botón: el menú vuelve a aparecer solo debajo.
  dontShowNextButton: true,
  nextQuestionIndex: "mainMessage",
  routine() {},
});

// Informative step with rich text only (no image cards). getAnswers resolves an
// empty list so ChatMessageComponent still auto-advances to the hub.
const textStep = (name, text) => ({
  name,
  text,
  type: "informative",
  nextQuestionIndex: "mainMessage",
  answersLoaded: false,
  answers: [],
  getAnswers() {
    if (this.answersLoaded) return Promise.resolve(this.answers);
    return new Promise((resolve) => {
      this.answersLoaded = true;
      resolve(this.answers);
    });
  },
  routine() {},
});

export default {
  name: "PieDemo",
  components: { ChatMessageComponent },
  // Absorb the props the layout's router-view binds so they don't leak onto the
  // root element; none of them are needed by the demo.
  props: {
    bannerLoaded: { type: Boolean, required: false, default: false },
    routeName: { type: String, required: false, default: "" },
    tags: { type: Array, required: false, default: () => [] },
  },
  data() {
    return {
      mainColor: "#c6588d",
      secondaryColor: "#d98bb0",
      currentQuestionIndex: "",
      condition: "",
      context: {},
      messages: [],
      questions: {
        // First step: an animation + explanations of everything the platform
        // does for a school (massive student load via spreadsheet, info
        // management, simultaneous editing, monthly usage reports, info shared
        // between documents, sending the FUDEI directly, and more). Then it
        // hands off to the service menu (welcomeMessage).
        intro: {
          name: "intro",
          text: `👋 ¡Hola! Antes de conocer cada servicio, mira todo lo que el <strong>Sistema PIE</strong> hace por tu colegio:`,
          type: "custom-component",
          component: PieIntroAnimation,
          single_choice: false,
          answersLoaded: true,
          answers: [],
          // No continue button: the menu (welcomeMessage) is shown right below
          // the animation as soon as it renders.
          dontShowNextButton: true,
          nextQuestionIndex: "welcomeMessage",
          routine() {},
        },
        welcomeMessage: {
          name: "welcomeMessage",
          text: `Con el <strong>Sistema PIE</strong> tu colegio tiene <strong>usos ILIMITADOS</strong> de todos nuestros servicios.<br/>
            ¿Cuál te gustaría conocer en detalle?`,
          type: "options",
          answersLoaded: false,
          single_choice: true,
          answers: [],
          getAnswers(context) {
            if (this.answersLoaded) return Promise.resolve(this.answers);
            this.answers = buildMenu(context);
            this.answersLoaded = true;
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        mainMessage: {
          name: "mainMessage",
          isMain: true,
          text(context) {
            const remaining = MENU.filter(
              (m) => !(m.nextIndex in context)
            ).length;
            if (remaining === 0) {
              return `¡Ya conoces todos los servicios! Y recuerda: con el <strong>Sistema PIE</strong> tu colegio los usa <strong>sin límites</strong>.<br/>Agenda una videollamada con nuestro equipo y te mostramos el Sistema PIE para tu colegio.`;
            }
            return `Con el <strong>Sistema PIE</strong> tu colegio tiene <strong>usos ilimitados</strong> de todos estos servicios. ¿Qué más te gustaría conocer?`;
          },
          type: "options",
          answersLoaded: false,
          single_choice: true,
          answers: [],
          getAnswers(context) {
            // Rebuild each turn so visited services drop off the menu.
            this.answers = buildMenu(context);
            this.answersLoaded = true;
            return Promise.resolve(this.answers);
          },
          routine() {},
        },
        eureka_info: serviceAnimStep(
          "eureka_info",
          "<strong>Eureka</strong> es nuestra prueba psicopedagógica estandarizada para el diagnóstico de DEA. Con el Sistema PIE la aplicas <strong>sin límites</strong>:",
          "#efba50",
          [
            {
              scene: "standardized",
              icon: "🧠",
              title: "Diagnóstico DEA estandarizado",
              text: "Validada bajo el <strong>Decreto 170</strong>: mide el progreso en lectoescritura y cálculo y entrega las desviaciones estándar.",
            },
            {
              scene: "online-test",
              icon: "💻",
              title: "Digital y se corrige en línea",
              text: "Los estudiantes la rinden desde un computador y la <strong>mayoría de las preguntas se corrigen solas</strong>.",
            },
            {
              scene: "report",
              icon: "📄",
              title: "Genera un informe psicopedagógico",
              text: "Obtienes un <strong>reporte completo</strong> que permite el diagnóstico de DEA e ingreso a PIE.",
            },
          ]
        ),
        planif_info: serviceAnimStep(
          "planif_info",
          "Las <strong>Planificaciones de clase</strong> se crean con IA, a tu medida. Con el Sistema PIE las generas <strong>sin límites</strong>:",
          "#20b2aa",
          [
            {
              scene: "oa-select",
              icon: "🎯",
              title: "Elige varios Objetivos de Aprendizaje",
              text: "Selecciona los <strong>OA de una asignatura y sus indicadores</strong>, todos los que necesites para tu clase.",
            },
            {
              scene: "details",
              icon: "📝",
              title: "Especifica los detalles que quieras",
              text: "Personaliza la planificación con <strong>cualquier detalle o instrucción adicional</strong> antes de generarla.",
            },
            {
              scene: "download",
              icon: "💾",
              title: "Lista para descargar",
              text: "La planificación se genera con IA en formato editable, lista para <strong>descargar a tu PC</strong>.",
            },
            {
              scene: "powerpoint",
              icon: "📊",
              title: "Genera una presentación PowerPoint",
              text: "Una vez creada la planificación, <strong>genera automáticamente una presentación PPT</strong> basada en ella.",
            },
          ]
        ),
        materials_info: serviceAnimStep(
          "materials_info",
          "Las <strong>Pruebas informales</strong> tienen dos usos, ambos <strong>ilimitados</strong> con el Sistema PIE:",
          "#e84855",
          [
            {
              scene: "self-correct",
              icon: "✅",
              title: "Aplica y corrige automáticamente",
              text: "Los estudiantes responden en línea y la <strong>mayoría de las preguntas se corrigen solas</strong>.",
            },
            {
              scene: "ai-generate",
              icon: "🤖",
              title: "Crea pruebas únicas con IA",
              text: "Indícanos el objetivo y el contexto y <strong>generamos preguntas especializadas</strong> automáticamente.",
            },
            {
              scene: "catalog",
              icon: "🏷️",
              title: "Vitrina de materiales premium",
              text: "Accede a un <strong>catálogo de pruebas listas</strong> para aplicar, creadas por nuestro equipo educativo.",
            },
          ]
        ),
        docs_info: serviceAnimStep(
          "docs_info",
          "La <strong>Documentación PIE</strong> elabora todos los documentos del Decreto 170 con sugerencias, transferencia de información e IA. Con el Sistema PIE, <strong>sin límites</strong>:",
          "#208ac6",
          [
            {
              scene: "checklist",
              icon: "🗂️",
              title: "Todos los documentos del Decreto 170",
              text: "PACI, Informe a la familia, FU de reevaluación, PAI, Estados de avance, Anamnesis y <strong>muchos más</strong>.",
            },
            {
              scene: "transfer",
              icon: "🔗",
              title: "Información que se transfiere sola",
              text: "Los datos <strong>pasan solos entre documentos</strong>, sin copiar y pegar una y otra vez.",
            },
            {
              scene: "suggestions",
              icon: "💡",
              title: "Sugerencias e inteligencia artificial",
              text: "Te <strong>sugerimos y redactamos con IA</strong> para crear documentos más rápido y con mejor calidad.",
            },
          ]
        ),
        tests_info: serviceAnimStep(
          "tests_info",
          "La <strong>Corrección de test del Decreto 170</strong>: incorpora los puntajes y obtén el informe en Word automáticamente. Con el Sistema PIE, <strong>sin límites</strong>:",
          "#b76629",
          [
            {
              scene: "scores-input",
              icon: "🔢",
              title: "Incorpora los puntajes",
              text: "Ingresas los puntajes del test y <strong>el sistema hace el resto</strong> de la corrección por ti.",
            },
            {
              scene: "self-correct",
              icon: "⚙️",
              title: "Corrige automáticamente",
              text: "Test psicológicos, fonoaudiológicos y de terapia ocupacional: <strong>WISC-V, WAIS-IV, ICAP, TEPROSIF-R</strong> y más.",
            },
            {
              scene: "word",
              icon: "📝",
              title: "Informe en Word listo",
              text: "Obtienes un <strong>informe en Word</strong> listo para descargar y editar.",
            },
          ]
        ),
        // The platform is contracted per school: the visitor leaves their data
        // and we set up a videocall to show it (redpieService.sendSalesContact).
        quote: {
          name: "quote",
          text: `¡Genial! Agenda una <strong>videollamada</strong> con nuestro equipo y te mostramos el <strong>Sistema PIE</strong> en acción, con <strong>usos ilimitados</strong> de todos los servicios. Déjanos tus datos y coordinamos la videollamada.`,
          type: "inputs",
          answersLoaded: true,
          answers: [
            {
              name: "name",
              type: "text",
              placeholder: "Tu nombre",
              class: "col-12 col-md-6 mb-2",
            },
            {
              name: "email",
              type: "email",
              placeholder: "Tu correo",
              class: "col-12 col-md-6 mb-2",
            },
            {
              name: "school",
              type: "text",
              placeholder: "Nombre de tu colegio",
              class: "col-12 mb-2",
            },
          ],
          nextMessage: "Agendar videollamada",
          extraFunction: (data) => {
            redpieService
              .sendSalesContact({
                name: data.name,
                email: data.email,
                message: `Solicitud de videollamada del Sistema PIE para el colegio "${data.school}". Contacto: ${data.email}.`,
              })
              .then(() => {
                this.$toasted.success(
                  "¡Solicitud enviada! Te contactaremos para coordinar tu videollamada."
                );
              })
              .catch(() => {
                this.$toasted.error(
                  "No pudimos enviar tu solicitud. Por favor, inténtalo nuevamente."
                );
              });
          },
          nextQuestionIndex: "quote_done",
          routine: () => {},
        },
        // Same cotización mechanism as the other services: the backend renders
        // the DOCX from the PIE BusinessProduct price × students and mails it.
        bill: {
          name: "bill",
          text: `¡Excelente! El <strong>Sistema PIE</strong> se contrata por colegio, con <strong>usos ilimitados</strong> de todos los servicios. Indícanos tu correo y la cantidad de estudiantes PIE y te enviaremos la cotización.`,
          type: "inputs",
          answersLoaded: true,
          answers: [
            {
              name: "email",
              type: "email",
              placeholder: "Escribe tu correo aquí",
              class: "col-12 col-md-6 mb-2",
            },
            {
              name: "applications",
              type: "number",
              placeholder: "Cantidad de estudiantes PIE",
              class: "col-12 col-md-6 mb-2",
            },
          ],
          nextMessage: "Obtener cotización!",
          extraFunction: (data) => {
            informationService
              .sendEurekaBill({
                email: data.email,
                applications: data.applications,
                service: "PIE",
              })
              .then((resp) => {
                this.$toasted.success(resp.message);
              });
          },
          nextQuestionIndex: "bill_done",
          routine: () => {},
        },
        bill_done: textStep(
          "bill_done",
          "¡Listo! Te enviamos la <strong>cotización</strong> del <strong>Sistema PIE</strong> a tu correo. Mientras tanto, ¿quieres seguir conociendo los servicios?"
        ),
        quote_done: textStep(
          "quote_done",
          "¡Gracias! Recibimos tu solicitud y te contactaremos muy pronto para coordinar una <strong>videollamada</strong> y mostrarte el <strong>Sistema PIE</strong> para tu colegio. Mientras tanto, ¿quieres seguir conociendo los servicios?"
        ),
      },
    };
  },
  created() {
    this.$store.commit("chatbot/clear");
    this.currentQuestionIndex = "intro";
    const question = this.questions[this.currentQuestionIndex];
    this.context[question.name] = {};
    this.messages.push(question);
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentQuestionIndex];
    },
  },
  methods: {
    handleCardClick(answer, msgidx, ansidx, max) {
      if (this.messages[msgidx].name === this.currentQuestion.name) {
        this.selectCard(msgidx, ansidx, max);
        this.selectAnswer();
      }
    },
    selectCard(msgidx, ansidx) {
      if (this.currentQuestion.single_choice) {
        this.messages[msgidx].answers.forEach((ans) => {
          ans.isSelected = false;
        });
      }
      this.messages[msgidx].answers[ansidx].isSelected =
        !this.messages[msgidx].answers[ansidx].isSelected;
    },
    selectAnswer() {
      if (this.currentQuestion.single_choice) {
        this.continueChat();
      }
    },
    async continueChat() {
      // Resolve the selected answer (for single-choice option steps) so we can
      // route on its nextQuestionIndex.
      if (
        this.currentQuestion.type === "options" &&
        this.currentQuestion.single_choice
      ) {
        this.context[this.currentQuestion.name] =
          this.currentQuestion.answers.find((ans) => ans.isSelected);
      }

      this.messages.slice(-1)[0].answered = true;
      if (this.currentQuestion.isFinal) return;

      await this.currentQuestion.routine();
      this.currentQuestionIndex =
        this.context[this.currentQuestion.name] &&
        this.context[this.currentQuestion.name].nextQuestionIndex
          ? this.context[this.currentQuestion.name].nextQuestionIndex
          : this.currentQuestion.nextQuestionIndex;
      this.loadQuestion(this.currentQuestionIndex);
    },
    loadQuestion(questionIndex) {
      try {
        const question = this.questions[questionIndex];
        this.context[question.name] = {};
        this.messages.push(question);
      } catch (error) {
        console.log(error);
      }
    },
    scrollToBottom() {
      const container = document.querySelector(".general-container");
      if (container) window.scrollTo(0, container.scrollHeight);
    },
    scrollToMsg(name) {
      const msgRef = this.$refs[`msgRef-${name}`];
      if (!msgRef || !msgRef.length) return;
      // La explicación de un servicio (`*_info`) añade el menú sola: hacerle
      // caso al scroll arrastraría al visitante más allá de lo que acaba de
      // abrir. La intro también se encadena sola, pero ahí sí se baja al saludo.
      const idx = this.messages.map((m) => m.name).lastIndexOf(name);
      if (idx > 0 && this.messages[idx - 1].name.endsWith("_info")) return;
      const parentMsg = msgRef.at(-1).$el.parentNode;
      parentMsg.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    },
  },
};
</script>
<style scoped>
.chat-box {
  margin: 10px auto;
  width: 100%;
  align-items: space-between;
  justify-content: space-between;
}
</style>
